import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom'; // Added import for navigate
import { farmerApi } from '../../../services/api';
import LoadingIndicator from '../../common/LoadingIndicator';

// Helper function to get the correct image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // If it's already a full URL
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // If it's a relative path
  return `http://127.0.0.1:8000${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
};

const FarmerDetail = ({ farmerId, onClose, onDelete }) => { // Added onDelete to props
  const navigate = useNavigate(); // Initialize navigate

  const [farmer, setFarmer] = useState(null);
  const [emissions, setEmissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emissionsLoading, setEmissionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emissionsError, setEmissionsError] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Function to handle image click
  const handleImageClick = (imageUrl) => {
    setEnlargedImage(imageUrl);
  };

  // Function to close enlarged image
  const handleCloseEnlargedImage = () => {
    setEnlargedImage(null);
  };

  useEffect(() => {
    const fetchFarmerDetails = async (id) => {
      try {
        setLoading(true);
        const data = await farmerApi.getFarmer(id);
        setFarmer(data);

        // Log image URLs for debugging
        if (data) {
          console.log('Farmer data:', data);
          if (data.farmer_photo) {
            console.log('Farmer photo URL:', getImageUrl(data.farmer_photo));
          }
          if (data.land_photo_1) {
            console.log('Land photo 1 URL:', getImageUrl(data.land_photo_1));
          }
        }

        setError(null);
      } catch (err) {
        setError(`Failed to fetch farmer details: ${err.message}`);
        console.error('Error fetching farmer details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (farmerId) {
      fetchFarmerDetails(farmerId);
    }
  }, [farmerId]);

  useEffect(() => {
    const fetchEmissionsData = async (id) => {
      if (!id) {
        console.warn('No farmer ID available for fetching emissions.');
        setEmissionsError('Farmer ID is missing for emissions data.');
        setEmissionsLoading(false);
        return;
      }
      try {
        setEmissionsLoading(true);
        // Fetch emissions data using the correct farmer UUID
        const emissionsData = await farmerApi.getFarmerEmissions(id);
        setEmissions(emissionsData);
        setEmissionsError(null);
      } catch (err) {
        setEmissionsError(`Failed to fetch emissions data: ${err.message}`);
        console.error('Error fetching emissions data:', err);
      } finally {
        setEmissionsLoading(false);
      }
    };

    if (farmer && farmer.id) {
      fetchEmissionsData(farmer.id);
    }
  }, [farmer]);

  if (loading || emissionsLoading) {
    return <LoadingIndicator message="Loading farmer details..." />;
  }

  if (error || emissionsError) {
    return <div style={{ color: 'red' }}>{error || emissionsError}</div>;
  }

  if (!farmer) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>🔍</div>
        <p style={styles.errorMessage}>Farmer not found</p>
        <button onClick={onClose} style={styles.closeButton}>
          Close
        </button>
      </div>
    );
  }

  const handleEdit = () => {
    navigate(`/admin/farmer-form/${farmerId}`); // Fixed navigate usage
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this farmer?')) {
      try {
        await farmerApi.deleteFarmer(farmerId);
        if (onDelete) {
          onDelete(farmerId); // Fixed onDelete usage
        }
        onClose();
      } catch (err) {
        setError(`Failed to delete farmer: ${err.message}`);
      }
    }
  };

  return (
    <>
      <div style={styles.overlay} onClick={onClose}></div>
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Farmer Details</h2>
          <div style={styles.headerActions}>
            <button onClick={handleEdit} style={styles.editButton} title="Edit Farmer">
              ✏️ Edit
            </button>
            <button onClick={handleDelete} style={styles.deleteButton} title="Delete Farmer">
              🗑️ Delete
            </button>
            <button onClick={onClose} style={styles.closeButton}>
              ✕
            </button>
          </div>
        </div>

      <div style={styles.content}>
        {/* System Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>System Information</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>ID</span>
              <span style={styles.infoValue}>{farmer.id || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Created At</span>
              <span style={styles.infoValue}>
                {farmer.created_at ? new Date(farmer.created_at).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Updated At</span>
              <span style={styles.infoValue}>
                {farmer.updated_at ? new Date(farmer.updated_at).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Sync Status</span>
              <span style={styles.infoValue}>{farmer.sync_status || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Personal Information</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Name</span>
              <span style={styles.infoValue}>{farmer.farmer_name || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Spouse Name</span>
              <span style={styles.infoValue}>{farmer.spouse_name || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Gender</span>
              <span style={styles.infoValue}>{farmer.gender || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Mobile</span>
              <span style={styles.infoValue}>{farmer.mobile || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Alt. Mobile</span>
              <span style={styles.infoValue}>{farmer.alt_mobile || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Govt ID</span>
              <span style={styles.infoValue}>{farmer.govt_id || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Location Information</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Village</span>
              <span style={styles.infoValue}>{farmer.village || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Mandal</span>
              <span style={styles.infoValue}>{farmer.mandal || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>District</span>
              <span style={styles.infoValue}>{farmer.district || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>State</span>
              <span style={styles.infoValue}>{farmer.state || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Pincode</span>
              <span style={styles.infoValue}>{farmer.pincode || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Land Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Land Information</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Acreage</span>
              <span style={styles.infoValue}>{farmer.acreage || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Land Status</span>
              <span style={styles.infoValue}>{farmer.land_status || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Product IDs</span>
              <span style={styles.infoValue}>{farmer.product_ids || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Organization Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Organization Information</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Has Organization</span>
              <span style={styles.infoValue}>{farmer.has_organization ? 'Yes' : 'No'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>FPO Name</span>
              <span style={styles.infoValue}>{farmer.fpo_name || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>FPG Name</span>
              <span style={styles.infoValue}>{farmer.fpg_name || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>FPG ID</span>
              <span style={styles.infoValue}>{farmer.fpg_id || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Social Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Social Information</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Category</span>
              <span style={styles.infoValue}>{farmer.category || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Literacy</span>
              <span style={styles.infoValue}>{farmer.literacy || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Children</span>
              <span style={styles.infoValue}>{farmer.children || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Livelihood</span>
              <span style={styles.infoValue}>{farmer.livelihood || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Annual Income</span>
              <span style={styles.infoValue}>{farmer.annual_income || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Other Source Income</span>
              <span style={styles.infoValue}>{farmer.other_source_income || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Total Income</span>
              <span style={styles.infoValue}>{farmer.total_income || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Crop Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Crop Information</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Crop Name</span>
              <span style={styles.infoValue}>{farmer.crop_name || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Crop Area</span>
              <span style={styles.infoValue}>
                {farmer.crop_area ? `${farmer.crop_area} ${farmer.crop_area_unit || 'acres'}` : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Yield Produced</span>
              <span style={styles.infoValue}>
                {farmer.yield_produced ? `${farmer.yield_produced} ${farmer.yield_unit || ''}` : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Cropping Season</span>
              <span style={styles.infoValue}>{farmer.cropping_season || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Cropping Pattern</span>
              <span style={styles.infoValue}>{farmer.cropping_pattern || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Farming Practice</span>
              <span style={styles.infoValue}>{farmer.farming_practice || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Agriculture Crop Type</span>
              <span style={styles.infoValue}>{farmer.agriculture_crop_type || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Soil Characteristics */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Soil Characteristics</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Soil Type</span>
              <span style={styles.infoValue}>{farmer.soil_type || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Soil pH</span>
              <span style={styles.infoValue}>{farmer.soil_ph || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>EC</span>
              <span style={styles.infoValue}>
                {farmer.ec ? `${farmer.ec} ${farmer.ec_unit || ''}` : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Organic Carbon</span>
              <span style={styles.infoValue}>
                {farmer.organic_carbon ? `${farmer.organic_carbon} ${farmer.organic_carbon_unit || ''}` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Fertilizer */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Fertilizer</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Fertilizer Type</span>
              <span style={styles.infoValue}>{farmer.fertilizer_type || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Application Rate</span>
              <span style={styles.infoValue}>
                {farmer.application_rate ? `${farmer.application_rate} ${farmer.application_rate_unit || ''}` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Crop Protection */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Crop Protection</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Pesticide Category</span>
              <span style={styles.infoValue}>{farmer.pesticide_category || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Pesticide Application Rate</span>
              <span style={styles.infoValue}>
                {farmer.pesticide_application_rate ?
                  `${farmer.pesticide_application_rate} ${farmer.pesticide_application_rate_unit || ''}` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Fuel & Energy */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Fuel & Energy</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Direct Energy Use</span>
              <span style={styles.infoValue}>{farmer.direct_energy_use || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Energy Used</span>
              <span style={styles.infoValue}>
                {farmer.energy_used ? `${farmer.energy_used} ${farmer.energy_unit || ''}` : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Energy Category</span>
              <span style={styles.infoValue}>{farmer.energy_category || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Irrigation */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Irrigation</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Water Source</span>
              <span style={styles.infoValue}>{farmer.water_source || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Irrigation Method</span>
              <span style={styles.infoValue}>{farmer.irrigation_method || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Power Source</span>
              <span style={styles.infoValue}>{farmer.power_source || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Power Consumption</span>
              <span style={styles.infoValue}>
                {farmer.power_consumption ? `${farmer.power_consumption} ${farmer.power_consumption_unit || ''}` : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Transport */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Transport</h3>
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Transport Activity Type</span>
              <span style={styles.infoValue}>{farmer.transport_activity_type || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Transport Mode</span>
              <span style={styles.infoValue}>{farmer.transport_mode || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Distance</span>
              <span style={styles.infoValue}>
                {farmer.distance ? `${farmer.distance} ${farmer.distance_unit || ''}` : 'N/A'}
              </span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Fuel Type</span>
              <span style={styles.infoValue}>{farmer.fuel_type || 'N/A'}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Fuel Consumption</span>
              <span style={styles.infoValue}>
                {farmer.fuel_consumption ? `${farmer.fuel_consumption} ${farmer.fuel_consumption_unit || ''}` : 'N/A'}
              </span>
            </div>
          </div>
        </div>


        {/* CO2 Emissions */}
        {emissions && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>CO2 Emissions</h3>
            <div style={styles.emissionsContainer}>
              <div style={styles.emissionsGrid}>
                <div style={styles.emissionsItem}>
                  <span style={styles.emissionsLabel}>Fertilizer CO2 Emissions</span>
                  <span style={styles.emissionsValue}>
                    {emissions.fertilizer_co2_emissions !== undefined
                      ? `${emissions.fertilizer_co2_emissions} kg CO2e`
                      : 'N/A'}
                  </span>
                </div>
                <div style={styles.emissionsItem}>
                  <span style={styles.emissionsLabel}>Pesticide CO2 Emissions</span>
                  <span style={styles.emissionsValue}>
                    {emissions.pesticide_co2_emissions !== undefined
                      ? `${emissions.pesticide_co2_emissions} kg CO2e`
                      : 'N/A'}
                  </span>
                </div>
                <div style={styles.emissionsItem}>
                  <span style={styles.emissionsLabel}>Energy CO2 Emissions</span>
                  <span style={styles.emissionsValue}>
                    {emissions.energy_co2_emissions !== undefined
                      ? `${emissions.energy_co2_emissions} kg CO2e`
                      : 'N/A'}
                  </span>
                </div>
                <div style={styles.emissionsItem}>
                  <span style={styles.emissionsLabel}>Irrigation CO2 Emissions</span>
                  <span style={styles.emissionsValue}>
                    {emissions.irrigation_co2_emissions !== undefined
                      ? `${emissions.irrigation_co2_emissions} kg CO2e`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              <div style={styles.totalEmissionsContainer}>
                <span style={styles.totalEmissionsLabel}>Total CO2 Emissions</span>
                <span style={styles.totalEmissionsValue}>
                  {emissions.total_co2_emissions !== undefined
                    ? `${emissions.total_co2_emissions} kg CO2e`
                    : 'N/A'}
                </span>
              </div>
              {emissionsLoading && (
                <div style={styles.emissionsLoadingIndicator}>
                  <div style={styles.loadingSpinner}></div>
                  <p>Loading emissions data...</p>
                </div>
              )}
              {emissionsError && (
                <div style={styles.emissionsError}>
                  {emissionsError}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Media Files */}
        {(farmer.farmer_photo || farmer.land_photo_1 || farmer.soil_characteristics ||
          farmer.fertilizer_photos || farmer.crop_protection_photos) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Media Files</h3>
            <div style={styles.mediaGrid}>
              {farmer.farmer_photo && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Farmer Photo</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.farmer_photo)}
                      alt="Farmer"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.farmer_photo))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
              {farmer.land_photo_1 && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Land Photo 1</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.land_photo_1)}
                      alt="Land 1"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.land_photo_1))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
              {farmer.land_photo_2 && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Land Photo 2</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.land_photo_2)}
                      alt="Land 2"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.land_photo_2))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
              {farmer.land_photo_3 && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Land Photo 3</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.land_photo_3)}
                      alt="Land 3"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.land_photo_3))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
              {farmer.land_photo_4 && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Land Photo 4</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.land_photo_4)}
                      alt="Land 4"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.land_photo_4))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
              {farmer.soil_characteristics && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Soil Characteristics</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.soil_characteristics)}
                      alt="Soil Characteristics"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.soil_characteristics))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
              {farmer.fertilizer_photos && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Fertilizer Photos</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.fertilizer_photos)}
                      alt="Fertilizer"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.fertilizer_photos))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
              {farmer.crop_protection_photos && (
                <div style={styles.mediaItem}>
                  <span style={styles.mediaLabel}>Crop Protection Photos</span>
                  <div style={styles.imageContainer}>
                    <img
                      src={getImageUrl(farmer.crop_protection_photos)}
                      alt="Crop Protection"
                      style={styles.mediaImage}
                      onClick={() => handleImageClick(getImageUrl(farmer.crop_protection_photos))}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/200x150?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Enlarged Image View */}
      {enlargedImage && (
        <div style={styles.enlargedImageOverlay} onClick={handleCloseEnlargedImage}>
          <div style={styles.enlargedImageContainer}>
            <img
              src={enlargedImage}
              alt="Enlarged view"
              style={styles.enlargedImage}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              style={styles.closeEnlargedButton}
              onClick={handleCloseEnlargedImage}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '80vh',
    overflow: 'auto',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #eee',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 10,
  },
  headerActions: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  title: {
    margin: 0,
    color: '#2c3e50',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#7f8c8d',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  content: {
    padding: '1.5rem',
  },
  section: {
    marginBottom: '2rem',
  },
  sectionTitle: {
    color: '#2c3e50',
    borderBottom: '1px solid #eee',
    paddingBottom: '0.5rem',
    marginTop: 0,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '1rem',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0.5rem',
  },
  infoLabel: {
    fontSize: '0.85rem',
    color: '#7f8c8d',
    marginBottom: '0.25rem',
  },
  infoValue: {
    fontSize: '1rem',
    color: '#2c3e50',
    fontWeight: '500',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
    color: '#7f8c8d',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '1rem',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
    color: '#e74c3c',
  },
  errorIcon: {
    fontSize: '2rem',
    marginBottom: '1rem',
  },
  errorMessage: {
    marginBottom: '1rem',
    color: '#e74c3c',
    padding: '0.75rem',
    backgroundColor: '#ffebee',
    borderRadius: '4px',
    border: '1px solid #ffcdd2',
  },
  mediaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1.5rem',
  },
  mediaItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0.5rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  mediaLabel: {
    fontSize: '0.9rem',
    color: '#2c3e50',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
  },
  imageContainer: {
    width: '100%',
    height: '200px',
    overflow: 'hidden',
    borderRadius: '4px',
    backgroundColor: '#f1f1f1',
  },
  mediaImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
    cursor: 'pointer',
  },
  emissionsContainer: {
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  emissionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  emissionsItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0.75rem',
    backgroundColor: 'white',
    borderRadius: '6px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  emissionsLabel: {
    fontSize: '0.85rem',
    color: '#7f8c8d',
    marginBottom: '0.5rem',
  },
  emissionsValue: {
    fontSize: '1.1rem',
    color: '#2c3e50',
    fontWeight: '500',
  },
  totalEmissionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginTop: '1rem',
  },
  totalEmissionsLabel: {
    fontSize: '1rem',
    color: '#2e7d32',
    marginBottom: '0.5rem',
    fontWeight: 'bold',
  },
  totalEmissionsValue: {
    fontSize: '1.5rem',
    color: '#2e7d32',
    fontWeight: 'bold',
  },
  emissionsLoadingIndicator: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
    color: '#7f8c8d',
  },
  emissionsError: {
    padding: '0.75rem',
    backgroundColor: '#ffebee',
    color: '#c62828',
    borderRadius: '4px',
    marginTop: '1rem',
    border: '1px solid #ef9a9a',
  },
  enlargedImageOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
  },
  enlargedImageContainer: {
    position: 'relative',
    maxWidth: '90%',
    maxHeight: '90%',
    backgroundColor: 'white',
    borderRadius: '4px',
    overflow: 'hidden',
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
  },
  enlargedImage: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '90vh',
    objectFit: 'contain',
  },
  closeEnlargedButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '16px',
  },
};

export default FarmerDetail;

